import chunk from 'lodash/chunk';
import { influencer } from '@round/api';
import { useCreatorExploreListAllQueries } from '@round/creatorbase-helpers/api-hooks';
import { useInstagramUserImageQueriesByIds } from '@round/platforms/instagram';
import { useTiktokUserImageQueriesByIds } from '@round/platforms/tiktok';
import { PAGE_SIZE } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { extractUserIdsFromData } from 'Modules/Plans/CreatorSearch/utils/creatorSearchImageUtils';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { usePoolDetail } from '../../../hooks/usePoolDetail';

// The pool's creators are the creator-explore results filtered to that pool's memberships. Loading every
// page keeps the pool table showing all members at once, with no pagination affordance on the page.
export function usePoolCreators(poolId: number | null | undefined) {
    const { selectedClientId } = useClientSelect();

    // The creators list only makes sense once the pool itself has loaded: don't fetch members for a pool
    // whose detail request failed, and surface that failure as the table's error/loading state.
    const { pool, isLoading: isPoolLoading, isFetching: isPoolFetching, isError: isPoolError } = usePoolDetail(poolId);

    // A pool that reports no members has nothing to fetch: skip the creator-explore request entirely and
    // render the empty state at once, rather than spinning on a request we know comes back empty (e.g. right
    // after creating a pool). member_count is refetched when creators are added, which re-enables this query.
    const isPoolEmpty = pool?.member_count === 0;

    const { data, count, isLoading, isFetching, hasError, areAllPagesResolved } = useCreatorExploreListAllQueries(
        {
            client_id: selectedClientId,
            pool_id: typeof poolId === 'number' ? String(poolId) : undefined,
            page_size: PAGE_SIZE,
        },
        { enabled: typeof poolId === 'number' && typeof selectedClientId === 'number' && !!pool && !isPoolEmpty }
    );

    // Avatars are only worth fetching once the member list has settled.
    const { tiktokUserIds, instagramUserIds } = extractUserIdsFromData(areAllPagesResolved ? data : []);

    // Split ids into page-sized batches so each image request's GET URL stays within length limits;
    // the member list is loaded in full, so a single request could otherwise pack in every creator's id.
    const tiktokImageQueries = useTiktokUserImageQueriesByIds(chunk(tiktokUserIds, PAGE_SIZE));
    const instagramImageQueries = useInstagramUserImageQueriesByIds(chunk(instagramUserIds, PAGE_SIZE));

    const tiktokUserImages = tiktokImageQueries.flatMap((query) => query.data ?? []);
    const instagramUserImages = instagramImageQueries.flatMap((query) => query.data ?? []);

    const getCreatorImages = (creator: influencer.CreatorSearchResult | {} | null | undefined) => {
        if (!creator || !('platforms' in creator)) {
            return { tiktok: null, instagram: null };
        }

        const tiktokImage = tiktokUserImages.find(
            (image) => image.user_id === creator.platforms.tiktok[0]?.creatorbase_user_id
        );
        const instagramImage = instagramUserImages.find(
            (image) => image.user_id === creator.platforms.instagram[0]?.creatorbase_user_id
        );

        return {
            tiktok: tiktokImage?.avatar_thumb.cached_url ?? tiktokImage?.avatar_thumb.original_url,
            instagram: instagramImage?.avatar_thumb.cached_url ?? instagramImage?.avatar_thumb.original_url,
        };
    };

    const areCreatorImagesLoading =
        tiktokImageQueries.some((query) => query.isLoading) || instagramImageQueries.some((query) => query.isLoading);

    return {
        creators: data,
        // With the fetch skipped for an empty pool, fall back to the pool's own member_count so the
        // header still reads "0 creators" instead of dropping the count label.
        count: count ?? pool?.member_count,
        // While the pool detail is still loading the members can't be fetched yet, so keep the table in its
        // loading state; a failed pool detail becomes the table's error rather than an empty-pool state.
        isLoading: isPoolLoading || isLoading,
        isFetching: isPoolFetching || isFetching,
        hasError: isPoolError || hasError,
        areAllPagesResolved,
        getCreatorImages,
        getAreCreatorImagesLoading: () => areCreatorImagesLoading,
    };
}
